import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthActions } from '@core/ngrx/actions';
import { State } from '@core/ngrx/reducers';
import { Store } from '@ngrx/store';
import { ApolloClient } from '@apollo/client/core';
import { signOut } from 'aws-amplify/auth';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { NavController } from '@ionic/angular';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { GrowthBookService } from '../growthbook/growthbook.service';

const helper = new JwtHelperService();

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    isLoginSubject: any = new BehaviorSubject<boolean>(this.hasToken());

    constructor(
        private http: HttpClient,
        private cookie: CookieService,
        @Optional() @Inject(ApolloClient) private apollo: ApolloClient,
        private store: Store<State>,
        private navController: NavController,
        private router: Router,
        private location: Location,
        private growthBookService: GrowthBookService
    ) {}

    private hasToken(): boolean {
        return this.cookie.check('amoactkn');
    }

    cognitoActivate(code: string, email: string, pwd: string) {
        return this.http.post(`${environment.API}/auth/confirm-signup`, { code, email, pwd });
    }

    cognitoResendCode(email: string) {
        return this.http.post(`${environment.API}/auth/confirm-signup`, { action: 'resend', email });
    }

    resendForgotPasswordVerificationCode(email: string) {
        return this.http.post(`${environment.API}/auth/forgot-password`, { email: email });
    }

    private CognitoLogin(data: object) {
        data['clientId'] = environment.COGNTIO_APP_CLIENT_ID;
        return this.http.post(`${environment.API}/auth/login?ngsw-bypass=true`, data);
    }

    amoCognitoLogin(data: object) {
        return this.CognitoLogin(data);
    }

    // Host-only: auth cookies are scoped to this subdomain (app.amopportunities.org)
    // so the trainee app and the admin app keep separate cookie jars (TP2020-11188).
    // The manager program-search hand-off carries its session explicitly via #h=.
    private getCookieDomain(): string {
        return window.location.hostname;
    }

    private clearAuthCookie(name: string): void {
        const cookieDomain = this.getCookieDomain();
        this.cookie.delete(name, '/', cookieDomain);
        // Also clear a host-only variant — some legacy paths set cookies without a domain.
        this.cookie.delete(name, '/');
    }

    /**
     * Set the post-login cookies. `amoactkn` is a sentinel cookie marking logged-in state for
     * synchronous auth guards; the HTTP interceptor reads the real access token from Amplify per
     * request. `mgmt_token` is what central-landing / app.component's `noUser` query var keys off
     * to route admins to the admin app — set it for admins, clear it for trainees so a stale value
     * from a prior management session can't misroute a trainee.
     */
    setLoginCookies(isAdmin: boolean): void {
        const cookieDomain = this.getCookieDomain();
        const isSecure = window.location.protocol === 'https:';

        this.cookie.delete('amoactkn', '/', cookieDomain);
        if (isAdmin) {
            this.cookie.set('mgmt_token', 'amo_management', 0, '/', cookieDomain, isSecure, 'Lax');
        } else {
            this.cookie.delete('mgmt_token', '/', cookieDomain);
        }
        this.cookie.set('amoactkn', '1', 0, '/', cookieDomain, isSecure, 'Lax');
    }

    /** when a user hits logout */
    logout = async (): Promise<void> => {
        this.growthBookService.clearUser();
        try {
            await signOut();
        } catch (err) {
            console.log('Amplify signOut error', err);
        }
        // clear client state regardless
        this.apollo.cache.reset();
        this.apollo.clearStore();
        this.store.dispatch(AuthActions.setLogout({ is_authenticated: false }));
        const cookieDomain = this.getCookieDomain();
        // NB: '_seg_' (analytics) intentionally omitted — isolation touches only auth cookies.
        ['amoactkn', 'XSRF-TOKEN', 'amosess.sid', 'role', 'mgmt_token', 'mfa-session', '_bp_', 'amo_app_pool'].forEach((name) => this.clearAuthCookie(name));
        this.cookie.deleteAll('/', cookieDomain);
        this.cookie.deleteAll('/');
        this.redirectLogin();
    };

    private redirectLogin() {
        setTimeout(() => {
            const cookieDomain = this.getCookieDomain();
            this.cookie.deleteAll('/', cookieDomain);
            this.cookie.deleteAll('/');
            const url = this.router.createUrlTree([`/login`]).toString();
            this.location.replaceState(url);
            location.reload();
        }, 1100);
    }

    //
    saveAgreement = (token: string, pdf: string): Observable<any> => {
        const decodedToken = helper.decodeToken(token);
        const data = { userId: decodedToken._id, base64: pdf };
        return this.http.put(`${environment.API}/users/processGeneralAgreement`, data);
    };

    // updates the user value of termsAgreed when being prompted with the login policy update component
    updateAgreePrompt = (id: string) =>
        this.http.put(`${environment.API}/users/${id}/useragreed`, {
            userId: id,
        });

    /**
     *
     * @param data email and mfa code
     */
    cognitoMFA(data: any) {
        return this.http.post(`${environment.API}/auth/verify-mfa-code`, data);
    }

    // check to see if manager is already in user pool and resend onboarding email.
    confirmActivation(body: object): Observable<any> {
        return this.http.post(`${environment.API}/managements/confirmactivation`, body);
    }
}
